import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button, TextField, Header } from '../layout';

const MaintenanceMode = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
    const [maintenanceMessage, setMaintenanceMessage] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        fetchMaintenanceStatus();
    }, []);

    const fetchMaintenanceStatus = async () => {
        try {
            setIsLoading(true);
            const response = await api.getMaintenanceStatus();
            if (response.status === 'success' && response.maintenance) {
                setMaintenanceEnabled(response.maintenance.enabled);
                setMaintenanceMessage(response.maintenance.message);
            }
        } catch (error) {
            setError('Errore nel caricamento delle impostazioni di manutenzione');
            console.error('Error fetching maintenance status:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggleMaintenance = async () => {
        try {
            setIsSaving(true);
            setError('');
            setSuccess('');

            const response = await api.toggleMaintenanceMode({
                enabled: !maintenanceEnabled,
                message: maintenanceMessage
            });

            if (response.status === 'success') {
                setMaintenanceEnabled(!maintenanceEnabled);
                setSuccess(`Modalità manutenzione ${!maintenanceEnabled ? 'attivata' : 'disattivata'} con successo`);
            }
        } catch (error) {
            setError('Errore durante l\'aggiornamento della modalità manutenzione');
            console.error('Error toggling maintenance mode:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveMessage = async () => {
        try {
            setIsSaving(true);
            setError('');
            setSuccess('');

            const response = await api.toggleMaintenanceMode({
                enabled: maintenanceEnabled,
                message: maintenanceMessage
            });

            if (response.status === 'success') {
                setSuccess('Messaggio di manutenzione aggiornato con successo');
            }
        } catch (error) {
            setError('Errore durante l\'aggiornamento del messaggio di manutenzione');
            console.error('Error updating maintenance message:', error);
        } finally {
            setIsSaving(false);
        }
    };

    if (!user || user.role !== 'admin') {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
                <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
                    <h1 className="text-2xl font-bold text-red-600 mb-4">Accesso negato</h1>
                    <p className="text-gray-700 mb-6">Non hai i permessi necessari per accedere a questa pagina.</p>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigate('/dashboard')}
                        fullWidth
                    >
                        Torna alla Dashboard
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <Header title="Modalità Manutenzione" />

            <div className="flex-grow container mx-auto px-4 py-8">
                <div className="bg-white rounded-lg shadow-md p-6">
                    {isLoading ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
                        </div>
                    ) : (
                        <>
                            {error && (
                                <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
                                    {error}
                                </div>
                            )}

                            {success && (
                                <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 text-green-700">
                                    {success}
                                </div>
                            )}

                            <div className="mb-8">
                                <h2 className="text-xl font-semibold mb-4">Stato Attuale</h2>
                                <div className="flex items-center mb-6">
                                    <span className="mr-4">Modalità Manutenzione:</span>
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${maintenanceEnabled
                                        ? 'bg-red-100 text-red-800'
                                        : 'bg-green-100 text-green-800'
                                        }`}>
                                        {maintenanceEnabled ? 'Attiva' : 'Disattiva'}
                                    </span>
                                </div>

                                <Button
                                    variant="contained"
                                    color={maintenanceEnabled ? "success" : "error"}
                                    onClick={handleToggleMaintenance}
                                    disabled={isSaving}
                                    className="mb-6"
                                >
                                    {isSaving ? 'Salvataggio...' : maintenanceEnabled
                                        ? 'Disattiva Modalità Manutenzione'
                                        : 'Attiva Modalità Manutenzione'
                                    }
                                </Button>

                                <div className="mt-8">
                                    <h3 className="text-lg font-medium mb-3">Messaggio di Manutenzione</h3>
                                    <p className="text-sm text-gray-600 mb-3">
                                        Questo messaggio verrà mostrato agli utenti quando la modalità manutenzione è attiva.
                                    </p>

                                    <TextField
                                        multiline
                                        rows={4}
                                        value={maintenanceMessage}
                                        onChange={(e) => setMaintenanceMessage(e.target.value)}
                                        placeholder="Inserisci il messaggio di manutenzione..."
                                        className="w-full mb-4"
                                    />

                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={handleSaveMessage}
                                        disabled={isSaving}
                                    >
                                        {isSaving ? 'Salvataggio...' : 'Salva Messaggio'}
                                    </Button>
                                </div>
                            </div>

                            <div className="mt-8 p-4 bg-red-50 rounded-lg">
                                <h3 className="text-lg font-medium text-red-800 mb-2">Informazioni</h3>
                                <p className="text-sm text-red-700">
                                    Quando la modalità manutenzione è attiva, solo gli amministratori potranno accedere all'applicazione.
                                    Tutti gli altri utenti vedranno il messaggio di manutenzione.
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MaintenanceMode;